import type {
	BreakpointsVariantsTo,
	CssFeature,
	IDomApplierProvider } from '@wix/thunderbolt-catharsis-extensions'
import {
	compCssMappersFeatureWrapper,
	createComponentTraversal,
	createCssFunctions,
	VSM_CSS_FEATURES,
	VSM_CSS_RESPONSIVE_MASTER_PAGE_FEATURES,
} from '@wix/thunderbolt-catharsis-extensions'
import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import { removeHash } from '@wix/thunderbolt-becky-root'
import type { RunAndReportFn } from '@wix/thunderbolt-state-manager'
import { ViewerStateManager, getDataNodes, REPEATER_DELIMITER } from '@wix/thunderbolt-state-manager'
import type { BreakpointsData, TimeAnimationOptions } from '@wix/thunderbolt-becky-types'
import { getNodesFromFeatures } from './catharsisLoader'
import type { CompVariantsViewItem, WithOverrides } from '@wix/thunderbolt-catharsis'
import { getCSSAnimation } from '@wix/motion'
import _ from 'lodash'

export type BreakpointToEffectMap = BreakpointsVariantsTo<TimeAnimationOptions | null>

const createVsm = (beckyModel: BeckyModel, runAndReport: RunAndReportFn) => {
	const isExperimentOpen = (experimentName: keyof BeckyModel['experiments']) =>
		!!beckyModel.experiments[experimentName]
	const compCssMappersFeature = compCssMappersFeatureWrapper(false)(
		beckyModel.componentsMappers,
		isExperimentOpen,
		runAndReport
	)
	const isResponsiveMasterPage = beckyModel.isResponsive && beckyModel.pageCompId === 'masterPage'
	const featuresToLoad = isResponsiveMasterPage ? VSM_CSS_RESPONSIVE_MASTER_PAGE_FEATURES : VSM_CSS_FEATURES
	const features: Record<string, CssFeature<any>> = { ...featuresToLoad, compCssMappers: compCssMappersFeature }
	const activeFeatures = _.pickBy(features, (feature, featureName) => {
		if (beckyModel.featuresToIgnore?.includes(featureName)) {
			return false
		}
		if (beckyModel.featuresToRun?.length && !beckyModel.featuresToRun.includes(featureName)) {
			return false
		}
		if (feature.experimentName !== 'OPEN_TO_ALL' && !isExperimentOpen(feature.experimentName)) {
			return false
		}
		if (feature.shouldRun && !feature.shouldRun(beckyModel)) {
			return false
		}
		return true
	})
	const { nodes, renderableNodeToFeatures } = getNodesFromFeatures(activeFeatures)
	const vsm = new ViewerStateManager(beckyModel, getDataNodes(), nodes, renderableNodeToFeatures, runAndReport)
	return {
		vsm,
		features,
	}
}

const getByIdWithOverrides = <T>(compId: string, response: any) => {
	const compResponse: T = response[compId].compCssMappers.compCssMappers
	if (typeof compResponse === 'undefined') {
		return compResponse as WithOverrides<T>
	}
	const allValues: WithOverrides<T> = { '': compResponse }

	const children = Object.keys(response).filter((id) => id.startsWith(compId + REPEATER_DELIMITER))
	children.forEach((childId) => {
		const childValue = response[childId].compCssMappers.compCssMappers
		if (childValue) {
			allValues[childId.split(REPEATER_DELIMITER)[1]] = childValue
		}
	})
	return allValues
}

function runDomAppliers(
	response: any,
	beckyModel: BeckyModel,
	structure: BeckyModel['structure'],
	domApplierProvider: IDomApplierProvider,
	pageCompId: string
) {
	const getCompById = (compId: string) => response[compId]
	const compCssMappersGetByIdWithOverrides: <T>(id: string) => WithOverrides<T> = (id: string) =>
		getByIdWithOverrides(id, response)
	const structureStoreGetById = (id: string) => structure[id]
	const traverseComponents = createComponentTraversal(
		getCompById,
		compCssMappersGetByIdWithOverrides,
		beckyModel.viewMode === 'mobile',
		domApplierProvider,
		structureStoreGetById
	)

	const pageComp = structure[pageCompId]
	return traverseComponents(pageComp, undefined, {}, {})
}

function addBreakpointsOrderAndCompCssData(data: BeckyModel['data'], structure: BeckyModel['structure'], response: {}) {
	const variantsData = data.variants_data
	const breakpointsData = Object.entries(variantsData).reduce<Record<string, BreakpointsData>>((acc, [, item]) => {
		if (item.type === 'BreakpointsData') {
			// @ts-ignore
			acc[item.id] = { ...item, values: item.values.map((v) => variantsData[removeHash(v)]) }
		}
		return acc
	}, {})
	Object.entries(structure).forEach(([id, comp]) => {
		// @ts-ignore
		response[id].breakpointsOrder =
			comp.breakpointVariantsQuery && breakpointsData[comp.breakpointVariantsQuery].values.length
				? breakpointsData[comp.breakpointVariantsQuery]
				: undefined
		// @ts-ignore
		response[id].compSpecificData.patterns = {
			// @ts-ignore
			[id]: comp.patternsQuery ? response[id].compSpecificData.patterns : undefined,
		}
	})
}

function calculateFontsCss(vsmResponse: any) {
	const fontFaceCssArray = Object.values(vsmResponse).flatMap(
		(compCss: any) => compCss?.fontFaces?.fontFacesCss || []
	)
	return [...new Set(fontFaceCssArray)].join('')
}

function getOffset(offset: number | undefined, count: number, index: number) {
	return typeof offset === 'number' ? offset : index / (count - 1)
}

function keyframePropertyToCSS(key: string): string {
	if (key === 'cssFloat') {
		return 'float'
	}
	if (key === 'easing') {
		return 'animation-timing-function'
	}
	return key.replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`)
}

function generateKeyframesCss(keyframes: Array<Record<string, string | number | undefined>>): string {
	const count = keyframes.length

	return keyframes
		.map(({ offset, ...keyframe }, index) => {
			const declaration = Object.entries(keyframe)
				.filter(([key, value]) => key !== 'offset' && value !== undefined && value !== null)
				.map(([key, value]) => `${keyframePropertyToCSS(key)}: ${value};`)
				.join(' ')

			return `${getOffset(offset as number, count, index) * 100}% { ${declaration} }`
		})
		.join('\n  ')
}

function calculateKeyframesCss(vsmResponse: any) {
	const renderedNames: Set<string> = new Set()

	const keyframesCssArray = Object.values(vsmResponse).flatMap((compCss: any) =>
		compCss?.motionTimeAnimations?.motionTimeAnimations?.flatMap(
			(animation: { data: BreakpointToEffectMap; variants: CompVariantsViewItem }) => {
				const keyframesCss: Set<string> = new Set()

				if (animation.data) {
					Object.values(animation.data).forEach((variation) => {
						Object.values(variation).forEach((animationOptions) => {
							const effectName = animationOptions?.namedEffect?.type

							if (effectName && !renderedNames.has(effectName)) {
								renderedNames.add(effectName)
								const effects = getCSSAnimation('stub', animationOptions)

								effects.forEach((effect) => {
									if (effect.keyframes && effect.keyframes.length > 0) {
										const keyframesName = effect.name || effectName

										if (!renderedNames.has(keyframesName)) {
											renderedNames.add(keyframesName)
											const keyframes = generateKeyframesCss(effect.keyframes)

											keyframesCss.add(`@keyframes ${keyframesName} {\n  ${keyframes}\n}`)
										}
									}
								})
							}
						})
					})
				}

				return [...keyframesCss]
			}
		)
	)

	return [...new Set(keyframesCssArray)].join('')
}

export const getViewerStateManagerCss = (beckyModel: BeckyModel, runAndReport: RunAndReportFn = (fn: any) => fn()) => {
	const { vsm, features } = createVsm(beckyModel, runAndReport)
	const structure = vsm.getCurrentStructure()
	const response = runAndReport(() => vsm.compute(), 'getToViewItems')

	addBreakpointsOrderAndCompCssData(beckyModel.data, structure, response)
	const domApplierProvider = (featureName: string) => features[featureName].domApplier

	const css = runAndReport(
		() => runDomAppliers(response, beckyModel, structure, domApplierProvider, beckyModel.pageCompId),
		'runDomAppliers'
	)

	const { stringifyComponentsCssData } = createCssFunctions(features, domApplierProvider)

	const [fontsCss, keyframesCss, allCompsCss] = runAndReport(
		() => [
			beckyModel.featuresToRun?.length ? '' : calculateFontsCss(response),
			calculateKeyframesCss(response),
			stringifyComponentsCssData(css),
		],
		'stringifyCssData'
	)

	return {
		css,
		megaStore: {},
		vsmObject: response,
		cssResult: fontsCss + keyframesCss + allCompsCss,
	}
}
